import React, { useId, useState } from "react";
import {
  Box,
  Button,
  ExpandableSection,
  SpaceBetween,
  Table,
  TableProps,
} from "@amzn/awsui-components-react";
import {
  OmsExportPreset,
  OmsPresetScope,
  OmsReport,
} from "../../lusca-search-api/generated-src";
import { OmsConfiguration } from "./omsPresetEditing";
import presetChanges, {
  PresetChange,
  PresetChangeValue,
} from "./omsPresetChanges";
import { comparisonLabel } from "./omsReportFormatting";
import { submittedCondition } from "./omsFilterEditing";

function PresetChangeCell({
  value,
  report,
}: {
  value: PresetChangeValue;
  report: OmsReport;
}) {
  const [expanded, setExpanded] = useState(false);
  const detailsId = useId();
  const { filter } = value;
  const text = filter
    ? comparisonLabel(filter.operator, report.columnTypes?.[filter.column])
    : value.text;
  const values = filter
    ? filter.values || (filter.value !== undefined ? [filter.value] : [])
    : value.values || [];
  const labels = values.map((item) => (item === "" ? '"" (blank)' : item));
  let unit = "column";
  if (filter) unit = "value";
  else if (value.kind === "filters") unit = "filter";
  if (labels.length > 3 || labels.join(", ").length > 160)
    return (
      <SpaceBetween size="xs">
        {text && <span>{text}</span>}
        <Button
          ariaExpanded={expanded}
          ariaControls={detailsId}
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? "Hide" : "Show"} {labels.length}{" "}
          {labels.length === 1 ? unit : `${unit}s`}
        </Button>
        <div id={detailsId} hidden={!expanded}>
          {expanded && (
            <ol className="oms-report-download__applied-filters">
              {labels.map((item, index) => (
                // Duplicate values are possible in an ordered filter-field list.
                // eslint-disable-next-line react/no-array-index-key
                <li key={index}>{item}</li>
              ))}
            </ol>
          )}
        </div>
      </SpaceBetween>
    );
  return (
    <span>
      {text}
      {text && labels.length > 0 && " "}
      {labels.join(", ")}
    </span>
  );
}

function changeColumns(
  report: OmsReport,
  saved: OmsExportPreset
): TableProps.ColumnDefinition<PresetChange>[] {
  const savedReport = { ...report, columnTypes: saved.schemaTypes };
  return [
    {
      id: "setting",
      header: "Setting",
      width: 180,
      cell: (change) => change.label,
    },
    {
      id: "before",
      header: "Saved",
      cell: (change) => (
        <PresetChangeCell value={change.before} report={savedReport} />
      ),
    },
    {
      id: "after",
      header: "Updated",
      cell: (change) => (
        <PresetChangeCell value={change.after} report={report} />
      ),
    },
  ];
}

type PresetSetting = {
  id: string;
  label: string;
  value: PresetChangeValue;
};

function settingColumns(
  report: OmsReport
): TableProps.ColumnDefinition<PresetSetting>[] {
  return [
    {
      id: "setting",
      header: "Setting",
      width: 320,
      minWidth: 260,
      cell: (setting) => setting.label,
    },
    {
      id: "value",
      header: "To save",
      cell: (setting) => (
        <PresetChangeCell value={setting.value} report={report} />
      ),
    },
  ];
}

export function OMSPresetContents({
  draft,
  scope,
  report,
}: {
  draft: OmsConfiguration;
  scope: OmsPresetScope;
  report: OmsReport;
}) {
  const [expanded, setExpanded] = useState(true);
  const settings: PresetSetting[] = [];
  if (scope !== "columns") {
    settings.push({
      id: "rows",
      label: "Rows to export",
      value: { text: draft.mode === "full" ? "All rows" : "Filtered rows" },
    });
    if (draft.mode === "filtered") {
      settings.push({
        id: "operation",
        label: "Matching rule",
        value: {
          text:
            draft.operation === "or"
              ? "Any condition (OR)"
              : "All conditions (AND)",
        },
      });
      draft.criteria.forEach((criterion, index) => {
        if (criterion.column)
          settings.push({
            id: `filter-${index}`,
            label: criterion.column,
            value: { text: "", filter: submittedCondition(criterion) },
          });
      });
    }
  }
  if (scope !== "filters") {
    const allColumns = draft.columns.strategy === "all";
    const names = allColumns ? report.columns : draft.columns.names;
    const selectedLabel = names.length
      ? "Selected columns (CSV order)"
      : "No columns selected";
    settings.push({
      id: "columns",
      label: "Output columns",
      value: {
        text: allColumns ? "All columns (report order)" : selectedLabel,
        values: names,
        kind: "columns",
      },
    });
  }
  return (
    <ExpandableSection
      headerText="Settings in this preset"
      expanded={expanded}
      onChange={({ detail }) => setExpanded(detail.expanded)}
    >
      {/* Keyboard users must be able to scroll the bounded preview. */}
      {/* eslint-disable jsx-a11y/no-noninteractive-tabindex */}
      {expanded && (
        <div
          className="oms-preset-changes"
          role="region"
          aria-label="Preset settings to save"
          tabIndex={0}
        >
          <Table<PresetSetting>
            variant="embedded"
            cellVerticalAlign="top"
            stickyHeader
            wrapLines
            items={settings}
            trackBy="id"
            columnDefinitions={settingColumns(report)}
          />
        </div>
      )}
      {/* eslint-enable jsx-a11y/no-noninteractive-tabindex */}
    </ExpandableSection>
  );
}

export default function OMSPresetChanges({
  saved,
  draft,
  name,
  scope,
  report,
}: {
  saved: OmsExportPreset;
  draft: OmsConfiguration;
  name: string;
  scope: OmsPresetScope;
  report: OmsReport;
}) {
  const changes = presetChanges(saved, draft, name, scope);
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  return (
    <SpaceBetween size="s">
      <Box variant="h3">Changes to “{saved.name}”</Box>
      {changes.summaries.length ? (
        <>
          <ul className="oms-report-download__applied-filters">
            {changes.summaries.map((summary) => (
              <li key={summary}>{summary}</li>
            ))}
          </ul>
          <ExpandableSection
            headerText={`Change details (${changes.details.length})`}
            expanded={detailsExpanded}
            onChange={({ detail }) => setDetailsExpanded(detail.expanded)}
          >
            {/* Keyboard users must be able to scroll the bounded detail region. */}
            {/* eslint-disable jsx-a11y/no-noninteractive-tabindex */}
            {detailsExpanded && (
              <div
                className="oms-preset-changes"
                role="region"
                aria-label="Saved and updated preset settings"
                tabIndex={0}
              >
                <Table<PresetChange>
                  variant="embedded"
                  cellVerticalAlign="top"
                  stickyHeader
                  wrapLines
                  items={changes.details}
                  trackBy="id"
                  columnDefinitions={changeColumns(report, saved)}
                />
              </div>
            )}
            {/* eslint-enable jsx-a11y/no-noninteractive-tabindex */}
          </ExpandableSection>
        </>
      ) : (
        <Box color="text-body-secondary">No settings changed.</Box>
      )}
    </SpaceBetween>
  );
}
